import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, IconButton, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useState } from 'react';
import { useDateRange } from '../../../hooks/useGetDateRange';
import { useUserDashboardSettings } from '../../../hooks/useUserDashboardSettings';
import { useGetAllEvents } from '../../../services/react-query/events';
import { EventTimeSpan } from '../../../types/Event';
import { UserConfigurableModule } from '../../../types/User';
import { Loading } from '../../../uikit';
import { containerStyles, controlSectionStyles, paperContainerStyles, tilesContainerStyles, controlButtonStyles, iconButtonStyles } from '../dashboardStyles';
import { EventFooter } from './EventFooter';
import { EventTile } from './EventTile';
import { useGetCurrentUserSettings } from '../../../services/react-query/users/useGetCurrentUserSettings';

interface IEventsPanelProps {
  currentEmployeeUuid?: string;
}

export function EventsPanel({ currentEmployeeUuid }: IEventsPanelProps) {
  const { dashboardSettings, updateSettings } = useUserDashboardSettings(UserConfigurableModule.EVENTS);
  const { data: currentUserSettings } = useGetCurrentUserSettings();
  const [page, setPage] = useState<number>(1);

  const employees = currentEmployeeUuid && dashboardSettings.ownership === 'my' ? [currentEmployeeUuid] : undefined;
  const date_range = useDateRange(dashboardSettings.filters?.span || EventTimeSpan.TODAY);

  const { data, isLoading } = useGetAllEvents({
    params: {
      page,
      sort: { start_at: 1 },
      per_page: 6,
      filters: { employees, date_range },
    },
  });
  if (isLoading) return <Loading />;

  const maxPage = data?.meta?.total_pages;

  const setNewPage = (newPage: number) => {
    // Disable pagination if maxPage is unavailable.
    if (!maxPage) return;
    if (newPage < 1 || newPage > maxPage) return;
    setPage(newPage);
  };

  const controlSection = (
    <Box sx={controlSectionStyles}>
      {/* Ownership persists in user settings */}
      {currentUserSettings?.settings.isAdmin ? (
        <ToggleButtonGroup
          exclusive
          value={dashboardSettings.ownership}
          onChange={(_event, nextVal) => nextVal && updateSettings({ ownership: nextVal })}
          size="small"
        >
          <ToggleButton sx={controlButtonStyles} value="my" disabled={!currentEmployeeUuid}>
            My Events
          </ToggleButton>
          <ToggleButton sx={controlButtonStyles} value="all">
            All Events
          </ToggleButton>
        </ToggleButtonGroup>
      ) : (
        <Typography variant="h6" sx={{ height: '3rem', fontWeight: 600, pt: 1 }}>
          Events
        </Typography>
      )}

      {/* Filters persist in user settings */}
      <TextField
        select
        slotProps={{
          input: {
            sx: { ...controlButtonStyles },
          },
        }}
        margin="none"
        sx={{ maxWidth: 120 }}
        value={dashboardSettings.filters?.span || EventTimeSpan.TODAY}
        onChange={(event) => updateSettings({ filters: { span: event.target.value as EventTimeSpan } })}
      >
        <MenuItem value={EventTimeSpan.TODAY}>Today</MenuItem>
        <MenuItem value={EventTimeSpan.TOMORROW}>Tomorrow</MenuItem>
        <MenuItem value={EventTimeSpan.WEEK}>This Week</MenuItem>
        <MenuItem value={EventTimeSpan.UPCOMING}>Upcoming</MenuItem>
      </TextField>

      <Box sx={{ whiteSpace: 'nowrap' }}>
        <IconButton sx={iconButtonStyles} onClick={() => setNewPage(page - 1)} disabled={page <= 1 || !maxPage}>
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton sx={iconButtonStyles} onClick={() => setNewPage(page + 1)} disabled={!maxPage || page >= maxPage}>
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ ...containerStyles, width: '33%' }}>
      {/* fits event tiles + footer */}
      <Box component={Paper} sx={paperContainerStyles}>
        {controlSection}
        {/* fits 9 event tiles */}
        <Box sx={tilesContainerStyles}>{_.map(data?.data, EventTile)}</Box>
        <EventFooter employee={employees?.[0]} date_range={date_range} />
      </Box>
    </Box>
  );
}
